export type DashboardVideoItem = {
  id: string;
  title: string;
  subtitle: string;
  goalType: string;
  goalTypeLabel: string;
  language: string;
  videoUrl: string;
  embedUrl: string;
  thumbnail: string;
  isYoutube: boolean;
  durationLabel?: string;
};

export type DashboardVideoGoalGroup = {
  goalType: string;
  goalTypeLabel: string;
  videos: DashboardVideoItem[];
};

export const GOAL_TYPE_LABELS: Record<string, string> = {
  UNDERSTAND_MY_HEALTH: "Understand My Health",
  HEALTH_AWARENESS: "Health Awareness",
  MANAGE_A_CONDITION: "Manage a Condition",
  CONDITION_MANAGEMENT: "Condition Management",
  LIFE_STAGE_AND_FAMILY: "Life Stage & Family",
  MIND_STRESS_AND_SLEEP: "Mind, Stress & Sleep",
  MENTAL_WELLNESS: "Mental Wellness",
  GET_FIT_AND_MOVE_MORE: "Get Fit & Move More",
  FITNESS_AND_ACTIVITY: "Fitness And Activity",
  EAT_AND_NOURISH_BETTER: "Eat & Nourish Better",
  NUTRITION_AND_DIET: "Nutrition And Diet",
};

/** Maps API goal-type codes to the translation keys used in aiDashboard.goalTypes. */
export const GOAL_TYPE_TRANSLATION_KEYS: Record<string, string> = {
  UNDERSTAND_MY_HEALTH: "UNDERSTAND_MY_HEALTH",
  HEALTH_AWARENESS: "UNDERSTAND_MY_HEALTH",
  MANAGE_A_CONDITION: "MANAGE_A_CONDITION",
  CONDITION_MANAGEMENT: "MANAGE_A_CONDITION",
  LIFE_STAGE_AND_FAMILY: "LIFE_STAGE_AND_FAMILY",
  MIND_STRESS_AND_SLEEP: "MIND_STRESS_AND_SLEEP",
  MENTAL_WELLNESS: "MIND_STRESS_AND_SLEEP",
  GET_FIT_AND_MOVE_MORE: "GET_FIT_AND_MOVE_MORE",
  FITNESS_AND_ACTIVITY: "FITNESS_AND_ACTIVITY",
  EAT_AND_NOURISH_BETTER: "EAT_AND_NOURISH_BETTER",
  NUTRITION_AND_DIET: "EAT_AND_NOURISH_BETTER",
};

export const resolveGoalTypeTranslationKey = (goalType: string): string => {
  const normalized = goalType.trim().toUpperCase();
  return GOAL_TYPE_TRANSLATION_KEYS[normalized] ?? normalized;
};

export const GOAL_TYPE_ACCENTS: Record<string, { color: string; soft: string }> = {
  UNDERSTAND_MY_HEALTH: { color: "#0ea5e9", soft: "rgba(14, 165, 233, 0.14)" },
  HEALTH_AWARENESS: { color: "#0ea5e9", soft: "rgba(14, 165, 233, 0.14)" },
  MANAGE_A_CONDITION: { color: "#8b5cf6", soft: "rgba(139, 92, 246, 0.14)" },
  CONDITION_MANAGEMENT: { color: "#8b5cf6", soft: "rgba(139, 92, 246, 0.14)" },
  MIND_STRESS_AND_SLEEP: { color: "#6366f1", soft: "rgba(99, 102, 241, 0.14)" },
  MENTAL_WELLNESS: { color: "#6366f1", soft: "rgba(99, 102, 241, 0.14)" },
  LIFE_STAGE_AND_FAMILY: { color: "#ec4899", soft: "rgba(236, 72, 153, 0.14)" },
  GET_FIT_AND_MOVE_MORE: { color: "#10b981", soft: "rgba(16, 185, 129, 0.14)" },
  FITNESS_AND_ACTIVITY: { color: "#10b981", soft: "rgba(16, 185, 129, 0.14)" },
  EAT_AND_NOURISH_BETTER: { color: "#f59e0b", soft: "rgba(245, 158, 11, 0.14)" },
  NUTRITION_AND_DIET: { color: "#f59e0b", soft: "rgba(245, 158, 11, 0.14)" },
};

const GOAL_TYPE_ORDER = [
  "UNDERSTAND_MY_HEALTH",
  "HEALTH_AWARENESS",
  "MANAGE_A_CONDITION",
  "CONDITION_MANAGEMENT",
  "MIND_STRESS_AND_SLEEP",
  "MENTAL_WELLNESS",
  "LIFE_STAGE_AND_FAMILY",
  "GET_FIT_AND_MOVE_MORE",
  "FITNESS_AND_ACTIVITY",
  "EAT_AND_NOURISH_BETTER",
  "NUTRITION_AND_DIET",
];

const YOUTUBE_VIDEO_ID_CAPTURE = "([-A-Za-z0-9_]{6,})";

const YOUTUBE_VIDEO_ID_PATTERNS = [
  new RegExp(String.raw`(?:youtube\.com/shorts/)${YOUTUBE_VIDEO_ID_CAPTURE}`, "i"),
  new RegExp(String.raw`(?:youtu\.be/)${YOUTUBE_VIDEO_ID_CAPTURE}`, "i"),
  new RegExp(String.raw`(?:youtube\.com/embed/)${YOUTUBE_VIDEO_ID_CAPTURE}`, "i"),
  new RegExp(String.raw`(?:youtube\.com/watch\?v=)${YOUTUBE_VIDEO_ID_CAPTURE}`, "i"),
  new RegExp(String.raw`(?:youtube\.com/v/)${YOUTUBE_VIDEO_ID_CAPTURE}`, "i"),
];

const coerceRowString = (value: unknown, fallback = ""): string => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return fallback;
};

export const extractYouTubeVideoId = (url: string): string | null => {
  const trimmed = url.trim();
  if (!trimmed) return null;

  for (const pattern of YOUTUBE_VIDEO_ID_PATTERNS) {
    const match = pattern.exec(trimmed);
    if (match?.[1]) return match[1];
  }

  return null;
};

export const getYouTubeThumbnail = (videoId: string): string =>
  `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

export const getYouTubeEmbedUrl = (videoId: string): string =>
  `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1`;

export const formatDurationSeconds = (totalSeconds: number): string => {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "";
  const safeTotal = Math.floor(totalSeconds);
  const hours = Math.floor(safeTotal / 3600);
  const minutes = Math.floor((safeTotal % 3600) / 60);
  const seconds = safeTotal % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

export const isYoutubeShortVideo = (url: string): boolean =>
  /youtube\.com\/shorts\//i.test(url);

const resolveDurationLabel = (source: Record<string, unknown>): string | undefined => {
  const labeled = coerceRowString(
    source.durationLabel ?? source.duration ?? source.videoDuration ?? source.video_duration
  );
  if (labeled) {
    if (/^\d+$/.test(labeled)) return formatDurationSeconds(Number(labeled));
    return labeled;
  }

  const secondsValue = source.durationSeconds ?? source.duration_seconds ?? source.length;
  if (typeof secondsValue === "number" && Number.isFinite(secondsValue)) {
    return formatDurationSeconds(secondsValue);
  }
  if (typeof secondsValue === "string" && /^\d+$/.test(secondsValue.trim())) {
    return formatDurationSeconds(Number(secondsValue.trim()));
  }

  return undefined;
};

export const getVideoDurationLabel = (video: DashboardVideoItem): string | null => {
  if (video.durationLabel) return video.durationLabel;
  if (isYoutubeShortVideo(video.videoUrl)) return "Short";
  return null;
};

const titleCaseToken = (token: string): string =>
  token ? `${token.charAt(0).toUpperCase()}${token.slice(1).toLowerCase()}` : "";

const resolveGoalTypeLabel = (goalType: string): string => {
  const translationKey = resolveGoalTypeTranslationKey(goalType);
  return (
    GOAL_TYPE_LABELS[goalType.toUpperCase()] ||
    GOAL_TYPE_LABELS[translationKey] ||
    translationKey
      .replaceAll("_", " ")
      .split(" ")
      .filter(Boolean)
      .map(titleCaseToken)
      .join(" ")
  );
};

export const resolvePreferredLanguage = (appLanguage: string): string =>
  appLanguage === "hi" ? "Hindi" : "English";

export const normalizeLanguageKey = (label: string): string => {
  const trimmed = label.trim();
  if (!trimmed) return "";
  const lower = trimmed.toLowerCase();
  if (lower === "en" || lower === "eng" || lower === "english") return "english";
  if (lower === "hi" || lower === "hin" || lower === "hindi") return "hindi";
  if (/हिंदी|हिन्दी/.test(trimmed)) return "hindi";
  if (/अंग्रेज़ी|अंग्रेजी/.test(trimmed)) return "english";
  return lower;
};

const coerceRowLanguage = (row: Record<string, unknown>, fallback = "English"): string =>
  coerceRowString(
    row.language ??
      row.lang ??
      row.locale ??
      row.languageCode ??
      row.language_code ??
      row.videoLanguage ??
      row.video_language
  ) || fallback;

const extractUrlList = (row: Record<string, unknown>): string[] => {
  const candidates: unknown[] = [
    row.urls,
    row.videoUrls,
    row.video_urls,
    row.links,
    row.link,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      const urls = candidate
        .filter((url) => typeof url === "string" && url.trim())
        .map((url) => (url as string).trim());
      if (urls.length > 0) return urls;
    }
    if (typeof candidate === "string" && candidate.trim()) {
      return [candidate.trim()];
    }
  }

  const single = coerceRowString(row.url ?? row.videoUrl ?? row.video_url);
  return single ? [single] : [];
};

const toUrlArray = (value: unknown): string[] => {
  if (typeof value === "string" && value.trim()) return [value.trim()];
  if (!Array.isArray(value)) return [];
  return value
    .filter((url) => typeof url === "string" && url.trim())
    .map((url) => (url as string).trim());
};

type VideoLanguageGroup = { language: string; urls: string[] };

const normalizeObjectVideoGroups = (
  raw: Record<string, unknown>
): VideoLanguageGroup[] =>
  Object.entries(raw).flatMap(([language, urls]) => {
    const list = toUrlArray(urls);
    return list.length > 0 ? [{ language, urls: list }] : [];
  });

const normalizeArrayVideoEntry = (entry: unknown): VideoLanguageGroup | null => {
  if (typeof entry === "string" && entry.trim()) {
    return { language: "English", urls: [entry.trim()] };
  }
  if (!entry || typeof entry !== "object") return null;

  const row = entry as Record<string, unknown>;
  const urls = extractUrlList(row);
  if (!urls.length) return null;

  return {
    language: coerceRowLanguage(row),
    urls,
  };
};

const normalizeVideoLanguageGroups = (raw: unknown): VideoLanguageGroup[] => {
  if (raw == null) return [];
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return normalizeObjectVideoGroups(raw as Record<string, unknown>);
  }
  if (!Array.isArray(raw)) return [];

  return raw
    .map(normalizeArrayVideoEntry)
    .filter((group): group is VideoLanguageGroup => group !== null);
};

const VIDEO_PAYLOAD_KEYS = [
  "videos",
  "healthInterestVideos",
  "health_interest_videos",
  "fitnessVideos",
  "fitness_videos",
  "userVideos",
  "user_videos",
  "healthVideos",
  "health_videos",
  "interestVideos",
  "interest_videos",
];

const looksLikeVideoEntry = (entry: unknown): boolean => {
  if (typeof entry === "string") {
    return entry.includes("http") || entry.includes("youtube");
  }
  if (!entry || typeof entry !== "object") return false;

  const row = entry as Record<string, unknown>;
  if (
    row.goalType ||
    row.goal_type ||
    row.healthInterestOptionId ||
    row.health_interest_option_id
  ) {
    return true;
  }

  const nestedVideos = row.videos ?? row.videoLinks ?? row.media;
  if (nestedVideos != null) return true;

  return extractUrlList(row).length > 0;
};

const DASHBOARD_VIDEOS_CACHE_PREFIX = "mytara:dashboardVideos:";

export const cacheDashboardVideos = (
  patientId: string,
  rawVideos: unknown
): void => {
  if (!patientId || !Array.isArray(rawVideos) || rawVideos.length === 0) return;
  try {
    localStorage.setItem(
      `${DASHBOARD_VIDEOS_CACHE_PREFIX}${patientId}`,
      JSON.stringify(rawVideos)
    );
  } catch {
    /* ignore quota errors */
  }
};

export const readCachedDashboardVideos = (patientId: string): unknown => {
  if (!patientId) return undefined;
  try {
    const raw = localStorage.getItem(`${DASHBOARD_VIDEOS_CACHE_PREFIX}${patientId}`);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const pickLanguageUrls = (
  languageGroups: Array<{ language?: string; urls?: string[] }>,
  preferredLanguage: string
): { language: string; urls: string[] } => {
  const normalized = languageGroups
    .map((group) => ({
      language: coerceRowString(group.language) || "English",
      urls: Array.isArray(group.urls)
        ? group.urls.filter((url) => typeof url === "string" && url.trim())
        : [],
    }))
    .filter((group) => group.urls.length > 0);

  const preferredKey = normalizeLanguageKey(preferredLanguage);

  const exact = normalized.find(
    (group) => normalizeLanguageKey(group.language) === preferredKey
  );
  if (exact) return exact;

  const english = normalized.find(
    (group) => normalizeLanguageKey(group.language) === "english"
  );
  if (english) return english;

  return normalized[0] ?? { language: preferredLanguage, urls: [] };
};

/** Reads dashboard video arrays from common API payload shapes. */
export const extractDashboardVideosPayload = (dashboardData: unknown): unknown => {
  if (dashboardData == null) return undefined;

  if (typeof dashboardData === "string") {
    try {
      return extractDashboardVideosPayload(JSON.parse(dashboardData));
    } catch {
      return undefined;
    }
  }

  if (Array.isArray(dashboardData)) {
    return dashboardData.length > 0 && looksLikeVideoEntry(dashboardData[0])
      ? dashboardData
      : undefined;
  }

  if (typeof dashboardData !== "object") return undefined;

  const visited = new Set<unknown>();
  const walk = (node: unknown, depth = 0): unknown => {
    if (node == null || depth > 8 || visited.has(node)) return undefined;
    visited.add(node);

    if (Array.isArray(node)) {
      return node.length > 0 && looksLikeVideoEntry(node[0]) ? node : undefined;
    }

    if (typeof node !== "object") return undefined;
    const root = node as Record<string, unknown>;

    for (const key of VIDEO_PAYLOAD_KEYS) {
      const candidate = root[key];
      if (Array.isArray(candidate) && candidate.length > 0) {
        return candidate;
      }
    }

    for (const key of ["data", "result", "body", "payload", "content", "response"]) {
      const found = walk(root[key], depth + 1);
      if (found) return found;
    }

    return undefined;
  };

  return walk(dashboardData);
};

/** API payload first; falls back to last successful cache for this patient. */
export const resolveDashboardVideosPayload = (
  dashboardData: unknown,
  patientId?: string
): unknown => {
  const fromApi = extractDashboardVideosPayload(dashboardData);
  if (Array.isArray(fromApi) && fromApi.length > 0) {
    if (patientId) cacheDashboardVideos(patientId, fromApi);
    return fromApi;
  }

  if (patientId) {
    const cached = readCachedDashboardVideos(patientId);
    if (cached) return cached;
  }

  return fromApi;
};

const sortGoalGroups = (groups: DashboardVideoGoalGroup[]): DashboardVideoGoalGroup[] =>
  [...groups].sort((a, b) => {
    const aIndex = GOAL_TYPE_ORDER.indexOf(a.goalType);
    const bIndex = GOAL_TYPE_ORDER.indexOf(b.goalType);
    const safeA = aIndex === -1 ? GOAL_TYPE_ORDER.length : aIndex;
    const safeB = bIndex === -1 ? GOAL_TYPE_ORDER.length : bIndex;
    return safeA - safeB || a.goalTypeLabel.localeCompare(b.goalTypeLabel);
  });

const parseRawVideoEntries = (rawVideos: unknown): unknown[] | null => {
  let entries = rawVideos;
  if (typeof entries === "string") {
    try {
      entries = JSON.parse(entries);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(entries) || entries.length === 0) return null;
  return entries;
};

const resolveRowGoalType = (row: Record<string, unknown>): string => {
  const category =
    row.healthInterestCategory && typeof row.healthInterestCategory === "object"
      ? (row.healthInterestCategory as Record<string, unknown>)
      : null;

  return coerceRowString(
    row.goalType ??
      row.goal_type ??
      row.categoryGoalType ??
      row.goal ??
      category?.goalType ??
      category?.goal_type
  ).toUpperCase();
};

const resolveRowLanguageGroups = (
  row: Record<string, unknown>
): VideoLanguageGroup[] => {
  const languageGroups = normalizeVideoLanguageGroups(
    row.videos ?? row.videoLinks ?? row.media
  );
  if (languageGroups.length > 0) return languageGroups;

  const directUrls = extractUrlList(row);
  return directUrls.length > 0 ? [{ language: coerceRowLanguage(row), urls: directUrls }] : [];
};

const createDashboardVideoItem = ({
  normalizedUrl,
  youtubeId,
  index,
  flatLength,
  healthInterestOptionId,
  goalType,
  goalTypeLabel,
  interestTitle,
  language,
  durationLabel,
}: {
  normalizedUrl: string;
  youtubeId: string | null;
  index: number;
  flatLength: number;
  healthInterestOptionId: string;
  goalType: string;
  goalTypeLabel: string;
  interestTitle: string;
  language: string;
  durationLabel?: string;
}): DashboardVideoItem => ({
  id: `${healthInterestOptionId}-${goalType}-${normalizeLanguageKey(language)}-${youtubeId ?? index}-${flatLength}`,
  title: interestTitle,
  subtitle: `${goalTypeLabel} · ${language}`,
  goalType,
  goalTypeLabel,
  language,
  videoUrl: normalizedUrl,
  embedUrl: youtubeId ? getYouTubeEmbedUrl(youtubeId) : normalizedUrl,
  thumbnail: youtubeId
    ? getYouTubeThumbnail(youtubeId)
    : "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800&auto=format&fit=crop&q=80",
  isYoutube: Boolean(youtubeId),
  durationLabel:
    durationLabel ?? (isYoutubeShortVideo(normalizedUrl) ? "Short" : undefined),
});

/** Maps dashboard `data.videos` into goalType groups and a flat playable list. */
export const mapDashboardVideosResponse = (
  rawVideos: unknown,
  appLanguage?: string
): { groups: DashboardVideoGoalGroup[]; flat: DashboardVideoItem[] } => {
  const entries = parseRawVideoEntries(rawVideos);
  if (!entries) return { groups: [], flat: [] };

  const mapAllLanguages = appLanguage == null || appLanguage === "";
  const preferredLanguage = resolvePreferredLanguage(appLanguage ?? "en");
  const groupsByGoal = new Map<string, DashboardVideoGoalGroup>();
  const seenUrls = new Set<string>();
  const flat: DashboardVideoItem[] = [];

  for (const entry of entries) {
    if (!entry || typeof entry !== "object") continue;

    const row = entry as Record<string, unknown>;
    const goalType = resolveRowGoalType(row);
    if (!goalType) continue;

    const interestTitle = coerceRowString(
      row.title ?? row.interestTitle ?? row.name,
      "Health Video"
    );
    const healthInterestOptionId =
      coerceRowString(row.healthInterestOptionId) ||
      coerceRowString(row.health_interest_option_id) ||
      coerceRowString(row.id) ||
      interestTitle;

    const languageGroups = resolveRowLanguageGroups(row);
    const languageSelections = mapAllLanguages
      ? languageGroups
          .map((group) => ({
            language: coerceRowString(group.language) || "English",
            urls: group.urls.filter((url) => typeof url === "string" && url.trim()),
          }))
          .filter((group) => group.urls.length > 0)
      : [pickLanguageUrls(languageGroups, preferredLanguage)];

    if (!languageSelections.length) continue;

    const rowDurationLabel = resolveDurationLabel(row);
    const goalTypeLabel = resolveGoalTypeLabel(goalType);
    if (!groupsByGoal.has(goalType)) {
      groupsByGoal.set(goalType, { goalType, goalTypeLabel, videos: [] });
    }
    const group = groupsByGoal.get(goalType)!;

    languageSelections.forEach(({ language, urls }) => {
      urls.forEach((url, index) => {
        const normalizedUrl = url.trim();
        const dedupeKey = `${goalType}:${normalizeLanguageKey(language)}:${normalizedUrl}`;
        if (seenUrls.has(dedupeKey)) return;
        seenUrls.add(dedupeKey);

        const youtubeId = extractYouTubeVideoId(normalizedUrl);
        const item = createDashboardVideoItem({
          normalizedUrl,
          youtubeId,
          index,
          flatLength: flat.length,
          healthInterestOptionId,
          goalType,
          goalTypeLabel,
          interestTitle,
          language,
          durationLabel: rowDurationLabel,
        });

        group.videos.push(item);
        flat.push(item);
      });
    });
  }

  return {
    groups: sortGoalGroups(
      Array.from(groupsByGoal.values()).filter((group) => group.videos.length > 0)
    ),
    flat,
  };
};

const LANGUAGE_SORT_ORDER = ["english", "hindi"];

export const VIDEO_LANGUAGE_OPTIONS = [
  { code: "en", label: "English" },
  { code: "hi", label: "Hindi" },
] as const;

const sortLanguageLabels = (labels: string[]): string[] =>
  [...labels].sort((a, b) => {
    const aKey = normalizeLanguageKey(a);
    const bKey = normalizeLanguageKey(b);
    const aIndex = LANGUAGE_SORT_ORDER.indexOf(aKey);
    const bIndex = LANGUAGE_SORT_ORDER.indexOf(bKey);
    const safeA = aIndex === -1 ? LANGUAGE_SORT_ORDER.length : aIndex;
    const safeB = bIndex === -1 ? LANGUAGE_SORT_ORDER.length : bIndex;
    return safeA - safeB || a.localeCompare(b);
  });

/** Collect distinct language labels available in the raw dashboard video payload. */
export const collectAvailableVideoLanguages = (rawVideos: unknown): string[] => {
  const entries = parseRawVideoEntries(rawVideos);
  if (!entries) return [];

  const languages = new Set<string>();
  for (const entry of entries) {
    if (!entry || typeof entry !== "object") continue;
    const languageGroups = resolveRowLanguageGroups(entry as Record<string, unknown>);
    languageGroups.forEach((group) => {
      const label = coerceRowString(group.language) || "English";
      languages.add(label);
    });
  }

  return sortLanguageLabels(Array.from(languages));
};

export const languageLabelToAppCode = (label: string): string => {
  const key = normalizeLanguageKey(label);
  if (key === "hindi") return "hi";
  if (key === "english") return "en";
  return key || label;
};

export const resolveVideoLanguageLabel = (
  videoLanguage: string,
  translate?: (key: string, fallback: string) => string
): string => {
  const key = normalizeLanguageKey(videoLanguage);
  if (key === "hindi") {
    return translate?.("languageHindi", "Hindi") ?? "Hindi";
  }
  if (key === "english") {
    return translate?.("languageEnglish", "English") ?? "English";
  }
  return videoLanguage;
};

export const appCodeToLanguageLabel = (appLanguage: string): string =>
  resolvePreferredLanguage(appLanguage);

export type DashboardVideosMapped = {
  groups: DashboardVideoGoalGroup[];
  flat: DashboardVideoItem[];
};

/** Keep only videos whose goalType is in `goalTypes` (empty = show all). */
export const filterDashboardVideosByGoals = (
  mapped: DashboardVideosMapped,
  goalTypes: string[]
): DashboardVideosMapped => {
  if (!goalTypes.length) return mapped;

  const allowed = new Set(goalTypes.map((goalType) => goalType.toUpperCase()));
  const groups = mapped.groups.filter((group) =>
    allowed.has(group.goalType.toUpperCase())
  );
  const flat = groups.flatMap((group) => group.videos);
  return { groups, flat };
};

export const resolveVideoLanguageFilterKey = (languageCode: string): string =>
  normalizeLanguageKey(languageCode) ||
  normalizeLanguageKey(resolvePreferredLanguage(languageCode));

/** Keep only videos that match the selected language code or label (`en` / `hi` / `Hindi`). */
export const filterDashboardVideosByLanguage = (
  mapped: DashboardVideosMapped,
  languageCode: string
): DashboardVideosMapped => {
  const targetKey = resolveVideoLanguageFilterKey(languageCode);
  const flat = mapped.flat.filter(
    (video) => normalizeLanguageKey(video.language) === targetKey
  );

  const groupsByGoal = new Map<string, DashboardVideoGoalGroup>();
  flat.forEach((video) => {
    if (!groupsByGoal.has(video.goalType)) {
      groupsByGoal.set(video.goalType, {
        goalType: video.goalType,
        goalTypeLabel: video.goalTypeLabel,
        videos: [],
      });
    }
    groupsByGoal.get(video.goalType)!.videos.push(video);
  });

  return {
    groups: sortGoalGroups(Array.from(groupsByGoal.values())),
    flat,
  };
};

export type DashboardVideoFilterState = {
  goalTypes?: string[];
  languageCode?: string;
};

export const applyDashboardVideoFilters = (
  mapped: DashboardVideosMapped,
  filters: DashboardVideoFilterState
): DashboardVideosMapped => {
  let result = mapped;
  if (filters.languageCode) {
    result = filterDashboardVideosByLanguage(result, filters.languageCode);
  }
  if (filters.goalTypes?.length) {
    result = filterDashboardVideosByGoals(result, filters.goalTypes);
  }
  return result;
};
